<template>
  <v-dialog
    v-model="visible"
    persistent
    width="1200"
    scrollable
  >
    <v-card :loading="loading">
      <v-card-title>
        <span class="headline">{{ $t("modal.updateCase.title") }}</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="updateCaseForm">
          <v-container fluid>
            <v-row>
              <!-- Local ID -->
              <v-col cols="2">
                <v-text-field
                  dense
                  :maxlength="20"
                  :label="$t('modal.updateCase.label.localId')"
                  v-model="editCase.localId"
                  :hint="$t('hint.localId')"
                  persistent-hint
                  :error-messages="uniqueIdErrors"
                  :error="uniqueIdErrors.length > 0"
                />
              </v-col>
              <!-- Type -->
              <v-col cols="2">
                <v-select
                  dense
                  :items="getEntityTypes"
                  v-model="editCase.type"
                  :label="$t('label.type')"
                >
                  <template v-slot:item="{ item }">
                    <span>{{ capitalizeFirstLetter(item) }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span>{{ capitalizeFirstLetter(item) }}</span>
                  </template>
                </v-select>
              </v-col>
              <v-spacer />
              <v-col cols="7">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('label.name')"
                  v-model="editCase.name"
                  :rules="[rules.required]"
                />
              </v-col>
            </v-row>
            <v-row>
              <!-- Case Group -->
              <v-col cols="4">
                <v-autocomplete
                  v-model="editCase.caseGroupId"
                  :items="caseGroups"
                  :label="$t('modal.addCase.label.caseGroups')"
                  :hint="$t('hint.caseGroups')"
                  clearable
                  persistent-hint
                  item-text="name"
                  item-value="id"
                  dense
                />
              </v-col>
              <v-col cols="1"></v-col>
              <v-col>
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('label.title')"
                  v-model="editCase.title"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="2">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.phoneNumber')"
                  v-model="editCase.phoneNumber"
                />
              </v-col>
              <v-col cols="2">
                <v-select
                  dense
                  clearable
                  :items="genders"
                  v-model="editCase.gender"
                  :label="$t('modal.updateCase.label.gender')"
                />
              </v-col>
              <v-col cols="1"></v-col>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.citizenship')"
                  v-model="editCase.citizenship"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  label="Nationality"
                  v-model="editCase.nationality"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-menu
                  v-model="dobPicker"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      dense
                      clearable
                      v-model="editCase.dob"
                      label="Date of Birth"
                      append-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    no-title
                    :show-current="false"
                    v-model="editCase.dob"
                    @input="dobPicker = false"
                  ></v-date-picker>
                </v-menu>
              </v-col>
              <v-col cols="1"></v-col>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.placeOfBirth')"
                  v-model="editCase.placeOfBirth"
                />
              </v-col>
            </v-row>
            <!-- Client custom labels -->
            <v-row v-if="getAccountSettings.customLabels">
              <v-col
                cols="3"
                v-for="(value, labelName) in getAccountSettings.customLabels"
                :key="labelName"
              >
                <v-select
                  :items="value"
                  :label="labelName"
                  v-model="editCase.customLabels[labelName]"
                  clearable
                  dense
                >
                </v-select>
              </v-col>
            </v-row>

            <!-- Client custom fields -->
            <v-row v-if="getAccountSettings.customFields">
              <v-col
                cols="3"
                v-for="fieldName in getAccountSettings.customFields"
                :key="fieldName"
              >
                <v-text-field
                  dense
                  :label="fieldName"
                  v-model="editCase.customFields[fieldName]"
                />
              </v-col>
            </v-row>

            <v-row
              no-gutters
              align="center"
            >
              <v-col cols="2">
                <h3>{{ $t("label.address") }}</h3>
              </v-col>
              <v-col>
                <v-divider />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="6">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.address1')"
                  v-model="editCase.address.address1"
                />
              </v-col>
              <v-col cols="2">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.address2')"
                  v-model="editCase.address.address2"
                />
              </v-col>
              <v-col cols="2">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.address3')"
                  v-model="editCase.address.address3"
                />
              </v-col>
              <v-col cols="2">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.address4')"
                  v-model="editCase.address.address4"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.city')"
                  v-model="editCase.address.city"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.stateOrProvince')"
                  v-model="editCase.address.stateOrProvince"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.postalCode')"
                  v-model="editCase.address.postalCode"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  dense
                  :maxlength="100"
                  :label="$t('modal.updateCase.label.country')"
                  v-model="editCase.address.country"
                />
              </v-col>
            </v-row>

            <!-- IDs -->
            <v-row>
              <v-col cols="12">
                <v-row align="center">
                  <v-col>
                    <h3>IDs</h3>
                  </v-col>
                  <v-col cols="1">
                    <v-btn
                      color="green lighten-1"
                      icon
                      @click="addId"
                    >
                      <v-icon dark>mdi-plus</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- Click to add IDs banner -->
                <v-row
                  v-if="!caseIds || caseIds.length == 0"
                  dense
                >
                  <v-alert
                    dense
                    outlined
                    type="info"
                    >Click
                    <v-icon
                      small
                      color="success"
                      >mdi-plus</v-icon
                    >
                    to add an ID.</v-alert
                  >
                </v-row>

                <!-- Current IDs loop -->
                <v-slide-y-transition
                  class="py-0"
                  group
                >
                  <template v-for="(caseId, index) in caseIds">
                    <v-sheet
                      :key="index"
                      outlined
                      class="pl-4 my-4 py-4"
                    >
                      <v-row>
                        <!-- Type -->
                        <v-col
                          cols="3"
                          v-if="caseId.newId"
                        >
                          <v-combobox
                            label="Type (Select or enter a type)"
                            class="text-body-1"
                            dense
                            :items="getIdTypes"
                            v-model="caseId.type"
                          />
                        </v-col>
                        <v-col
                          cols="3"
                          v-else
                        >
                          <div class="overline font-weight-black">Type</div>
                          {{ caseId.type }}
                        </v-col>
                        <!-- Description -->
                        <v-col
                          cols="3"
                          v-if="caseId.newId"
                        >
                          <v-text-field
                            label="Description"
                            class="text-body-1"
                            dense
                            hide-details
                            :maxlength="100"
                            v-model="caseId.description"
                          />
                        </v-col>
                        <v-col
                          cols="3"
                          v-else
                        >
                          <div class="overline font-weight-black">Description</div>
                          {{ caseId.description }}
                        </v-col>
                        <!-- Country -->
                        <v-col
                          cols="1"
                          v-if="caseId.newId"
                        >
                          <v-text-field
                            label="Country"
                            dense
                            class="text-body-1"
                            hide-details
                            :maxlength="100"
                            v-model="caseId.country"
                          />
                        </v-col>
                        <v-col
                          cols="1"
                          v-else
                        >
                          <div class="overline font-weight-black">Country</div>
                          {{ caseId.country }}
                        </v-col>
                        <!-- ID Number -->
                        <v-col
                          cols="4"
                          v-if="caseId.newId"
                        >
                          <v-text-field
                            label="ID Number"
                            dense
                            class="text-body-1"
                            hide-details
                            :maxlength="100"
                            v-model="caseId.idNumber"
                          />
                        </v-col>
                        <v-col
                          cols="4"
                          v-else
                        >
                          <div class="overline font-weight-black">ID Number</div>
                          {{ caseId.idNumber }}
                        </v-col>

                        <v-col cols="1">
                          <v-btn
                            color="red lighten-1"
                            dark
                            icon
                            @click="deleteId(index)"
                          >
                            <v-icon dark>mdi-close</v-icon>
                          </v-btn>
                        </v-col>
                        <v-col cols="3">
                          <v-menu
                            v-model="idIssueDatePicker[index]"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                label="Issue Date"
                                class="text-body-1"
                                dense
                                clearable
                                v-model="caseId.issueDate"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="caseId.issueDate"
                              :show-current="false"
                              @input="idIssueDatePicker[index] = false"
                            >
                            </v-date-picker>
                          </v-menu>
                        </v-col>
                        <v-col cols="3">
                          <v-menu
                            v-model="idExpirationDatePicker[index]"
                            :close-on-content-click="false"
                            transition="scale-transition"
                            offset-y
                            min-width="auto"
                          >
                            <template v-slot:activator="{ on, attrs }">
                              <v-text-field
                                label="Expiration Date"
                                class="text-body-1"
                                dense
                                clearable
                                v-model="caseId.expirationDate"
                                append-icon="mdi-calendar"
                                readonly
                                v-bind="attrs"
                                v-on="on"
                              ></v-text-field>
                            </template>
                            <v-date-picker
                              v-model="caseId.expirationDate"
                              :show-current="false"
                              @input="idExpirationDatePicker[index] = false"
                            >
                            </v-date-picker>
                          </v-menu>
                        </v-col>
                      </v-row>
                    </v-sheet>
                  </template>
                </v-slide-y-transition>
              </v-col>
            </v-row>
          </v-container>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn
          color="primary"
          text
          @click="onCancel"
          >{{ $t("label.cancel") }}</v-btn
        >
        <v-btn
          dark
          color="primary"
          @click="onSave"
          :loading="loading"
          >{{ $t("label.save") }}</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { getMinDateString } from "../../utils/dateUtils";
import { capitalizeFirstLetter } from "../../utils/utils";
import { updateCaseApi } from "../../api/caseApi";
const idStruct = { type: "", country: "", idNumber: "", newId: true };
export default {
  name: "EditCase",
  props: {
    selectedCase: {},
    caseGroups: {},
    visible: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    selectedCase: {
      handler(newCase) {
        // TODO  Is this used???
        if (newCase.identification && newCase.identification.length > 0) {
          this.caseIds = [...newCase.identification];
        } else {
          this.caseIds = []; // Important - this needs to be reset so this cases IDs don't show up if another case is edited.
        }
      },
    },
  },
  data() {
    return {
      caseIds: [],
      loading: false,
      dobPicker: false,
      rules: {
        required: (value) => !!value || "Required.",
      },
      uniqueIdErrors: [],
      genders: ["Male", "Female"],
    };
  },
  computed: {
    ...mapGetters(["getAccountSettings", "getEntityTypes", "getIdTypes"]),
    idIssueDatePicker() {
      return this.caseIds.map(() => false);
    },
    idExpirationDatePicker() {
      return this.caseIds.map(() => false);
    },
    editCase() {
      return {
        ...this.selectedCase,
        // The type field is being capitalized because the enum has no name field, the API is case insensitive and returns type settings capitalized but type fields not. So this is needed so 'Vessel' = 'vessel' in type select v-model
        // type: this.selectedCase.type ? this.selectedCase.type.charAt(0).toUpperCase() + this.selectedCase.type.slice(1) : undefined,
        type: this.selectedCase.type ? this.selectedCase.type : undefined,
        address: this.selectedCase.address ? this.selectedCase.address : {},
        passport: this.selectedCase.passport ? this.selectedCase.passport : {},
        customLabels: this.selectedCase.customLabels ? this.selectedCase.customLabels : {},
        customFields: this.selectedCase.customFields ? this.selectedCase.customFields : {},
        driverLicense: this.selectedCase.driverLicense ? this.selectedCase.driverLicense : {},
        cryptoWallet: this.selectedCase.cryptoWallet ? this.selectedCase.cryptoWallet : {},
        // identification: this.selectedCase.identification ? this.selectedCase.identification : {}, // Is this needed?
      };
    },
  },
  methods: {
    ...mapActions(["setGlobalAlert"]),
    updateCaseApi,
    getMinDateString,
    capitalizeFirstLetter,
    addId() {
      this.caseIds.unshift({ ...idStruct });
    },
    deleteId(index) {
      this.caseIds.splice(index, 1);
    },
    //submit - TODO catch error if case group was deleted prior to saving case
    async onSave() {
      if (!this.$refs.updateCaseForm.validate()) {
        return false;
      }

      this.uniqueIdErrors = [];

      this.loading = true;

      let caseRecordRequest = JSON.parse(JSON.stringify(this.editCase));

      if (!caseRecordRequest.driverLicense.licenseNumber) {
        caseRecordRequest.driverLicense = null;
      }

      caseRecordRequest.identification = this.caseIds;

      try {
        await this.updateCaseApi(this.editCase.id, caseRecordRequest).then((caseRecord) => {
          this.$emit("onConfirmed", caseRecord);
          this.setGlobalAlert({
            alert: true,
            color: "green",
            alertTitle: `Case ${caseRecord.name} Updated`,
          });
        });
      } catch (e) {
        if (e.response?.status === 409) {
          this.uniqueIdErrors = ["Local ID already exists"];
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertTitle: `A Case already exists with ID: ${this.editCase.localId}`,
            alertMessage: "Enter a different ID and try again.",
          });
        } else {
          console.log(JSON.stringify(e))
          this.setGlobalAlert({
            alert: true,
            color: "red",
            alertMessage: e.message ? e.message : this.$t("error.general.unhandled"),
          });
        }
      } finally {
        this.loading = false;
        this.$refs.updateCaseForm.resetValidation();
      }

      this.loading = false;
    },
    onCancel() {
      this.$emit("onCancelled");
      this.$refs.updateCaseForm.resetValidation();
      this.uniqueIdErrors = []; // This is needed here because component data is not reset like in AddCase.
    },
  },
};
</script>
